import type { Cancellation } from "./model.ts";

// Reading a booking's cancellation policy: steps by date, earliest first, each saying what comes back
// if it's cancelled by then (`until` is a date, meaning the end of that day, or a local time, both in
// the booking's own time zone). Shared by the reminder sender and the web view.

export type Step = Cancellation["steps"][number];

// The last moment a full refund is possible: the `until` of the last full-refund step.
export function freeUntil(policy: Cancellation | undefined): string | null {
  const free = policy?.steps.filter((s) => s.refund === "full") ?? [];
  return free.length ? (free[free.length - 1] as Step).until : null;
}

// Whether a step's `until` is still ahead at a local time ("2026-12-01T09:30" in the booking's zone).
export function stillOpen(until: string, localNow: string): boolean {
  return until.length === 10 ? localNow.slice(0, 10) <= until : localNow.slice(0, 16) <= until.slice(0, 16);
}

// The step a cancellation now would fall in, or null once none is left (or there are none): nothing
// comes back.
export function currentStep(policy: Cancellation | undefined, localNow: string): Step | null {
  return policy?.steps.find((s) => stillOpen(s.until, localNow)) ?? null;
}

// The local time at an instant in a time zone, as "2026-12-01T09:30".
export function localNow(now: Date, tz: string): string {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
      .formatToParts(now)
      .map((p) => [p.type, p.value]),
  );
  return `${parts["year"]}-${parts["month"]}-${parts["day"]}T${parts["hour"]}:${parts["minute"]}`;
}

// Whole days from one local date to another ("2026-11-28" to "2026-12-01": 3).
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(to.slice(0, 10)) - Date.parse(from.slice(0, 10))) / 86_400_000);
}
